import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { DatabaseIcon, ListOrderedIcon } from "lucide-react";
import { useState } from "react";

import { TrajectoryConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { conditional, custom, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Toggle } from "@/components/Toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { MultipleScenarios } from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardView/EditCriterionFields/Inputs/MultipleScenarios";
import { collectToolNamesFromColumn } from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/ScorecardView/EditCriterionFields/utils/extractTrajectoryTools";
import { useSheetDataStoreSafe } from "@/routes/smart-table/sheet-data-store-context";

const getScenariosFromConfig = (config: TrajectoryConfiguration | undefined): string[][] => {
  if (config?.accepted_scenarios && config.accepted_scenarios.length > 0) {
    return config.accepted_scenarios;
  }
  return [[]];
};

const hasNoToolsScenario = (config: TrajectoryConfiguration | undefined): boolean =>
  config?.accepted_scenarios?.length === 1 && config.accepted_scenarios[0]?.length === 0;

const validationSchema = createBlockSchema<TrajectoryConfiguration>()
  .requireName()
  .addRule(required("configuration.trace_source", "Trace source is required"))
  .addRule(
    conditional(
      "configuration.expected_source",
      payload => Boolean(payload.configuration?.expected_source),
      required("configuration.expected_source", "Expected tools column is required"),
    ),
  )
  .addRule(
    custom(
      "configuration",
      (_, payload) => {
        const config = payload.configuration;
        const hasExpectedSource = Boolean(config?.expected_source?.trim());
        const hasAcceptedScenarios = Boolean(config?.accepted_scenarios?.length);
        return Number(hasExpectedSource) + Number(hasAcceptedScenarios) === 1;
      },
      "Provide accepted scenarios or an expected tools column",
      ["configuration.accepted_scenarios", "configuration.expected_source"],
    ),
  )
  .build();

const TrajectoryBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<TrajectoryConfiguration>, "DefaultFields">) => {
    const block = useBlock<TrajectoryConfiguration>();
    const isReadonly = useIsReadonly();
    const sheetDataStore = useSheetDataStoreSafe();
    const [isImportMode, setIsImportMode] = useState(Boolean(block.configuration?.expected_source));
    const [scenarios, setScenarios] = useState(() => getScenariosFromConfig(block.configuration));
    const [isNoToolsMode, setIsNoToolsMode] = useState(() =>
      hasNoToolsScenario(block.configuration),
    );

    const traceSourceColumnId = block.configuration?.trace_source || "";
    const toolSuggestions = collectToolNamesFromColumn(sheetDataStore, traceSourceColumnId);

    const updateConfiguration = (patch: Partial<TrajectoryConfiguration>) => {
      block.setConfiguration({
        mode: block.configuration?.mode ?? "strict",
        trace_source: block.configuration?.trace_source || "",
        ...block.configuration,
        ...patch,
      });
    };

    const persistScenarios = (nextScenarios: string[][]) => {
      setScenarios(nextScenarios);
      const normalized = nextScenarios
        .map(scenario => scenario.map(tool => tool.trim()).filter(Boolean))
        .filter(scenario => scenario.length > 0);
      updateConfiguration({
        accepted_scenarios: normalized.length > 0 ? normalized : undefined,
        expected_source: undefined,
      });
    };

    const handleModeChange = (mode: TrajectoryConfiguration["mode"]) => {
      updateConfiguration({ mode });
    };

    const handleImportModeChange = (enabled: boolean) => {
      setIsImportMode(enabled);
      if (enabled) {
        updateConfiguration({
          accepted_scenarios: undefined,
          expected_source: block.configuration?.expected_source || "",
        });
        return;
      }
      if (isNoToolsMode) {
        updateConfiguration({
          accepted_scenarios: [[]],
          expected_source: undefined,
        });
      } else {
        persistScenarios(scenarios);
      }
    };

    const handleNoToolsModeChange = (enabled: boolean) => {
      setIsNoToolsMode(enabled);
      if (enabled) {
        updateConfiguration({
          accepted_scenarios: [[]],
          expected_source: undefined,
        });
        return;
      }
      persistScenarios(scenarios);
    };

    return (
      <>
        <ValidatedField name="Match mode" path="configuration.mode">
          <Select
            value={block.configuration?.mode || "strict"}
            onValueChange={handleModeChange}
            disabled={isReadonly}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a mode..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="strict">Strict (exact sequence)</SelectItem>
              <SelectItem value="non_strict">Non-strict (subsequence)</SelectItem>
            </SelectContent>
          </Select>
        </ValidatedField>

        <SectionGroup
          title="Scenario"
          description="Define expected tool call sequences"
          icon={<ListOrderedIcon className="h-4 w-4" />}
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-700">Import from Column</span>
              <BasicTooltip content="Enable to import expected tools from a dataset column instead of listing them directly">
                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
              </BasicTooltip>
            </div>
            <Toggle
              enabled={isImportMode}
              setEnabled={handleImportModeChange}
              disabled={isReadonly}
            />
          </div>

          {!isImportMode ? (
            <div className="mb-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-700">No tools should be called</span>
                <BasicTooltip content="Pass only when the agent trace contains no tool calls">
                  <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
                </BasicTooltip>
              </div>
              <Toggle
                enabled={isNoToolsMode}
                setEnabled={handleNoToolsModeChange}
                disabled={isReadonly}
              />
            </div>
          ) : null}

          {isImportMode && (
            <div className="space-y-4">
              <div className="text-xs">
                Import from a dataset column. Cells should be JSON with{" "}
                <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px]">
                  accepted_scenarios
                </code>
                , each containing{" "}
                <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px]">
                  required_tools
                </code>
                . One scenario is enough for a single sequence.
              </div>
              <SingleSourceSelector
                sources={sources}
                sourceKey="expected_source"
                validationPath="configuration.expected_source"
                name="Expected tools source"
                placeholder="Select a source..."
              />
            </div>
          )}

          {!isImportMode && isNoToolsMode && (
            <p className="text-xs text-gray-600">
              This scenario passes only when the Agent Trace contains no tool calls.
            </p>
          )}

          {!isImportMode && !isNoToolsMode && (
            <div className="space-y-4">
              <div className="text-xs">
                Add tool sequences below. Pass if any scenario matches the Agent Trace (OR).
              </div>
              <ValidatedField name="Expected tools" path="configuration.accepted_scenarios">
                <MultipleScenarios
                  scenarios={scenarios}
                  onScenariosChange={persistScenarios}
                  suggestions={toolSuggestions}
                  hasTraceSource={Boolean(traceSourceColumnId)}
                  disabled={isReadonly}
                />
              </ValidatedField>
            </div>
          )}
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the agent trace column to evaluate"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="trace_source"
            validationPath="configuration.trace_source"
            name="Trace column"
            placeholder="Select a trace column..."
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<TrajectoryConfiguration>(TrajectoryBlockCore, {
  validationSchema,
});
